/*
 * Copyright (c) 2024 SARL SIGMA POLYNESIA
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { ActionIcon, Autocomplete, AutocompleteProps } from '@mantine/core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTimes } from '@fortawesome/free-solid-svg-icons/faTimes'

export type SearchInputOption<T> = {
  disabled?: boolean,
  item: T
  label: string,
  value: string,
}

export type SearchInputRenderOption<T> = {
  checked: boolean,
  option: SearchInputOption<T>
}

export type SearchInputProps<T> = Omit<AutocompleteProps, 'onChange' | 'value'> & {
  data: SearchInputOption<T>[],
  onChange? (value?: string): void,
  onSearchChange? (search?: string): void,
  searchValue?: string,
  value?: string
}

function SearchInput<T> (props: SearchInputProps<T>) {
  const {
    data,
    disabled,
    id,
    rightSection,
    name,
    onChange,
    onSearchChange,
    searchValue,
    value,
    ...others
  } = props

  const intl = useIntl()
  const inputRef = useRef<HTMLInputElement>()
  const mouseOverOptions = useRef<boolean>(false)
  const isDirty = useRef<boolean>(false)
  const [lastSearch, setLastSearch] = useState<string>(searchValue)

  /**
   * Handles leaving field.
   */
  const handleBlur = useCallback(() => {
    const currentSearch = inputRef.current.value
    // Do nothing if mouse is over suggestion,
    // to allow the click event to be triggered.
    if (!mouseOverOptions.current) {
      // Restore last valid search if current search is not valid.
      if (searchValue !== currentSearch && isDirty.current) {
        onSearchChange(lastSearch)
      }
    }
  }, [lastSearch, onSearchChange, searchValue])

  /**
   * Handles change of field value.
   */
  const handleChange = useCallback((val?: string) => {
    isDirty.current = true
    // onSearchChange(val)
    onChange(val)
  }, [onChange])

  /**
   * Handles click on clear button.
   */
  const handleClearClick = useCallback(() => {
    isDirty.current = false
    setLastSearch(null)
    onSearchChange(null)
    onChange(null)
  }, [onChange, onSearchChange])

  useEffect(() => {
    // Clear search when value change.
    onSearchChange(null)
  }, [onSearchChange, value])

  return (
    <>
      <input
        name={name}
        type="hidden"
        value={value != null ? value : ''}
      />

      <Autocomplete
        placeholder={intl.formatMessage({
          id: 'search.dots',
          defaultMessage: 'rechercher...'
        })}
        {...others}
        data={data}
        disabled={disabled}
        id={id}
        onBlur={handleBlur}
        onChange={onSearchChange}
        onOptionSubmit={handleChange}
        ref={inputRef}
        value={searchValue || ''}
        rightSection={<>
          {value != null
            ? <ActionIcon
              disabled={disabled}
              onClick={handleClearClick}
              variant="subtle"
            >
              <FontAwesomeIcon
                fixedWidth
                icon={faTimes}
              />
            </ActionIcon>
            : null}
          {rightSection}
        </>}
      />
    </>
  )
}

export default SearchInput
